import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { PhoneIphone, Refresh } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { qrLoginService } from '../services/api';
import { useApp } from '../contexts/AppContext';
import { getReturnPath } from '../utils/navigation';

type Phase = 'loading' | 'waiting' | 'approved' | 'expired' | 'denied' | 'error';

interface QrLoginPanelProps {
  onBack?: () => void;
}

export const QrLoginPanel: React.FC<QrLoginPanelProps> = ({ onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getReturnPath(location.state);
  const { loginWithSession } = useApp();
  const [phase, setPhase] = useState<Phase>('loading');
  const [publicId, setPublicId] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimers = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    pollRef.current = null;
    tickRef.current = null;
  };

  const start = useCallback(async () => {
    stopTimers();
    setPhase('loading');
    try {
      const { publicId: id, ttlSeconds } = await qrLoginService.create();
      setPublicId(id);
      setSecondsLeft(ttlSeconds);
      setPhase('waiting');

      tickRef.current = setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);

      pollRef.current = setInterval(async () => {
        try {
          const res = await qrLoginService.status(id);
          if (res.status === 'APPROVED' && res.token && res.user) {
            stopTimers();
            setPhase('approved');
            loginWithSession(res.token, res.user);
            navigate(returnPath, { replace: true });
          } else if (res.status === 'EXPIRED') {
            stopTimers();
            setPhase('expired');
          } else if (res.status === 'DENIED') {
            stopTimers();
            setPhase('denied');
          }
        } catch {
          /* transient network error — keep polling */
        }
      }, 2000);
    } catch {
      setPhase('error');
    }
  }, [loginWithSession, navigate, returnPath]);

  useEffect(() => {
    start();
    return stopTimers;
  }, [start]);

  const approveUrl = publicId ? `${window.location.origin}/approve?lr=${publicId}` : '';

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Accedi con il telefono
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Inquadra il QR con la fotocamera del telefono (dove sei già connesso) e approva l'accesso.
      </Typography>

      {phase === 'loading' && (
        <Box sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {phase === 'waiting' && (
        <>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: 3,
              background: '#FFFFFF',
              border: '1px solid var(--border-color)',
            }}
          >
            <QRCodeSVG value={approveUrl} size={210} level="M" includeMargin={false} />
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--text-muted)' }}>
            {secondsLeft > 0 ? `Il codice scade tra ${secondsLeft}s` : 'Codice in scadenza…'}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8, color: 'var(--text-muted)' }}>
            <PhoneIphone fontSize="small" />
            <Typography variant="caption">In attesa di approvazione dal telefono</Typography>
          </Box>
        </>
      )}

      {phase === 'expired' && (
        <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
          Il codice QR è scaduto. Generane uno nuovo.
        </Alert>
      )}
      {phase === 'denied' && (
        <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
          Accesso rifiutato dal telefono.
        </Alert>
      )}
      {phase === 'error' && (
        <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
          Impossibile generare il codice QR. Riprova.
        </Alert>
      )}

      {(phase === 'expired' || phase === 'denied' || phase === 'error') && (
        <Button variant="contained" startIcon={<Refresh />} onClick={start} sx={{ mt: 1 }}>
          Genera nuovo QR
        </Button>
      )}

      {onBack && (
        <Box sx={{ mt: 2.5 }}>
          <Button variant="text" onClick={onBack} sx={{ color: 'var(--text-muted)' }}>
            ← Torna al login
          </Button>
        </Box>
      )}
    </Box>
  );
};

const QrLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: 'var(--surface)',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <QrLoginPanel onBack={() => navigate('/login', { state: location.state })} />
      </Box>
    </Box>
  );
};

export default QrLoginPage;
